import { ICON_CAVITY, ICON_CORE, ICON_CUBE, ICON_LEFT_FACE, ICON_LEFT_FACE_OPACITY, ICON_RIGHT_FACE, ICON_RIGHT_FACE_OPACITY, ICON_SHELL } from "../CoreIntelligenceIcon/core-intelligence-icon-paths.js";
//#region src/components/CoreIntelligenceLoader/core-intelligence-loader-geometry.ts
/**
* Geometry and timeline for `CoreIntelligenceLoader`.
*
* Coordinates are `CoreIntelligenceIcon` viewBox units (16 x 18). The mark is
* an isometric open cube sitting in a hexagonal shell with a solid core. While
* loading, the cube grows out to the shell's outline (eating the shell from the
* inside), rolls through turns that each land back on the same picture (see
* {@link LoaderMode}), and when told to stop finishes the turn in flight,
* shrinks back into the socket, and the shell grows back out around it.
*
* Every surface is `currentColor` at some opacity, so a frame is fully
* described by the visible face polygons, the cube's rounded outline (which
* clips those faces), the core's rounded silhouette, and the shell ring.
*
* The cube is modelled as a true cube, but the icon draws it slightly off
* isometric. Near rest the projected cube is warped onto the icon's corners
* (fading out as it grows), and the resting frame is the icon's own paths, so
* swapping between `CoreIntelligenceIcon` and the loader is seamless.
*/
const VIEWBOX_WIDTH = 16;
const VIEWBOX_HEIGHT = 18;
const CENTER = [8, 8.762];
/** Circumradius of the shell, its cavity, and the core (icon path data). */
const SHELL_RADIUS = 8 / Math.cos(Math.PI / 6);
const CAVITY_RADIUS = 5.3856;
const CORE_RADIUS = 3.078;
/** Cube silhouette at rest; it tucks under the shell past the cavity edge. */
const CUBE_RADIUS = 6;
/**
* Corner rounding as an equivalent fillet radius. The icon rounds its 120°
* corners with cubic segments whose tangent runs `radius * tan(30°)` along each
* edge, with handles at CORNER_HANDLE of that length.
*/
const SHELL_CORNER = 3.0502;
const CAVITY_CORNER = 1.5235;
const CORE_CORNER = .7618;
const CORNER_HANDLE = .382;
const PEAK_SCALE = SHELL_RADIUS / CUBE_RADIUS;
const ISO_TILT_DEG = Math.atan(Math.SQRT1_2) * (180 / Math.PI);
const REST_YAW_DEG = 45;
const HALF_EDGE = CUBE_RADIUS * Math.sqrt(3 / 2) / 2;
const CORE_HALF_EDGE = CORE_RADIUS * Math.sqrt(3 / 2) / 2;
const S = Math.SQRT1_2;
/**
* Face shading, lit from the front-left. The resting side faces have world
* normals with x = ±S, which lands them on the icon's two face opacities.
*/
const SHADE_BASE = (ICON_LEFT_FACE_OPACITY + ICON_RIGHT_FACE_OPACITY) / 2;
const SHADE_SIDE = (ICON_LEFT_FACE_OPACITY - ICON_RIGHT_FACE_OPACITY) / 2 / S;
/** Opacity of whichever face is on top once the cube has replaced the shell. */
const LID_OPACITY = .14;
/** Cube axes at the rest yaw, cycled through for successive turns. */
const TURN_AXES = [
	[
		0,
		1,
		0
	],
	[
		S,
		0,
		-S
	],
	[
		S,
		0,
		S
	]
];
const unit = (v) => {
	const length = Math.hypot(...v);
	return [
		v[0] / length,
		v[1] / length,
		v[2] / length
	];
};
/** Body diagonals at the rest yaw: a 120° turn about one is a symmetry too. */
const DIAGONAL_AXES = [
	unit([
		2 * S,
		1,
		0
	]),
	unit([
		0,
		1,
		2 * S
	]),
	unit([
		2 * S,
		-1,
		0
	])
];
const TIMING = {
	grow: 480,
	shrink: 480,
	shellReturn: 420,
	/** Gap before the first turn of a new mode, after the last one lands. */
	modeSwitchBeat: 60
};
const FIRST_TURN_AT = TIMING.grow - 90;
const MODES = {
	gyro: {
		spec: (n) => ({
			axis: TURN_AXES[n % 3],
			core: {
				axis: TURN_AXES[(n + 1) % 3],
				degrees: -90
			},
			degrees: 90
		}),
		turn: 800,
		turnEvery: 640
	},
	stepped: {
		spec: (n) => ({
			axis: TURN_AXES[n % 3],
			degrees: 90
		}),
		turn: 800,
		turnEvery: 640
	},
	tumble: {
		spec: (n) => ({
			axis: DIAGONAL_AXES[n % 3],
			degrees: 120
		}),
		turn: 900,
		turnEvery: 760
	}
};
const clamp01 = (x) => Math.min(1, Math.max(0, x));
/** Exact at both ends, so a finished tween lands on the resting value. */
const lerp = (a, b, k) => k >= 1 ? b : a + (b - a) * k;
const easeInOutCubic = (x) => x < .5 ? 4 * x ** 3 : 1 - (-2 * x + 2) ** 3 / 2;
const easeOutCubic = (x) => 1 - (1 - x) ** 3;
const easeInOutBack = (x, c) => {
	const c2 = c * 1.525;
	return x < .5 ? (2 * x) ** 2 * ((c2 + 1) * 2 * x - c2) / 2 : ((2 * x - 2) ** 2 * ((c2 + 1) * (x * 2 - 2) + c2) + 2) / 2;
};
/** Shell eaten from the inside as the cube grows to `g` (0..1) of full size. */
function eatenRing(g) {
	if (g >= .995) return null;
	return {
		inner: lerp(CAVITY_RADIUS, SHELL_RADIUS, g),
		innerCorner: lerp(CAVITY_CORNER, SHELL_CORNER, g),
		outer: SHELL_RADIUS,
		outerCorner: SHELL_CORNER
	};
}
/**
* One loading run, from the grow to the settle. Ask it for poses at times that
* never go backwards (ms since loading started).
*
* Turns are scheduled as time reaches them, each by the mode in force then. A
* mode change waits for the last turn to land before the next one starts, so
* a hand-over never cuts a turn short. Stopping lets the turns already started
* finish, then settles.
*/
var LoaderTimeline = class {
	constructor(mode = "stepped") {
		this.inFlight = [];
		this.count = 0;
		this.nextAt = FIRST_TURN_AT;
		this.last = null;
		this.exit = null;
		this.mode = mode;
	}
	/** The mode for turns from the next landing on. */
	setMode(mode) {
		this.mode = mode;
	}
	/** Stop at `ms`: start no more turns, and settle after the last one. */
	stop(ms) {
		if (this.exit) return this.exit;
		this.schedule(ms);
		const shrinkAt = this.last ? this.last.start + this.last.duration * .7 : Math.max(ms, TIMING.grow);
		const shellReturnAt = shrinkAt + TIMING.shrink * .55;
		this.exit = {
			end: shellReturnAt + TIMING.shellReturn,
			shellReturnAt,
			shrinkAt
		};
		return this.exit;
	}
	schedule(ms) {
		if (this.exit) return;
		for (;;) {
			const mode = MODES[this.mode];
			const start = this.last && this.last.mode !== this.mode ? Math.max(this.nextAt, this.last.start + this.last.duration + TIMING.modeSwitchBeat) : this.nextAt;
			if (start > ms) return;
			const turn = {
				...mode.spec(this.count++),
				duration: mode.turn,
				start
			};
			this.inFlight.push(turn);
			this.last = {
				...turn,
				mode: this.mode
			};
			this.nextAt = start + mode.turnEvery;
		}
	}
	poseAt(ms) {
		this.schedule(ms);
		const shrinkAt = this.exit?.shrinkAt ?? Infinity;
		let scale;
		let ring;
		if (ms < shrinkAt) {
			scale = lerp(1, PEAK_SCALE, easeInOutCubic(clamp01(ms / TIMING.grow)));
			ring = eatenRing((scale - 1) / (PEAK_SCALE - 1));
		} else {
			const { shellReturnAt } = this.exit;
			scale = lerp(PEAK_SCALE, 1, easeInOutCubic(clamp01((ms - shrinkAt) / TIMING.shrink)));
			const e = easeOutCubic(clamp01((ms - shellReturnAt) / TIMING.shellReturn));
			ring = e <= 0 ? null : {
				inner: CAVITY_RADIUS,
				innerCorner: CAVITY_CORNER,
				outer: lerp(CAVITY_RADIUS, SHELL_RADIUS, e),
				outerCorner: lerp(CAVITY_CORNER, SHELL_CORNER, e)
			};
		}
		this.inFlight = this.inFlight.filter((t) => ms < t.start + t.duration);
		const turns = [];
		const coreTurns = [];
		let coreDiffers = false;
		for (const turn of [...this.inFlight].reverse()) {
			if (ms <= turn.start) continue;
			const k = easeInOutBack((ms - turn.start) / turn.duration, .3);
			turns.push([turn.axis, turn.degrees * k]);
			coreTurns.push(turn.core ? [turn.core.axis, turn.core.degrees * k] : [turn.axis, turn.degrees * k]);
			coreDiffers ||= Boolean(turn.core);
		}
		return {
			done: this.exit !== null && ms >= this.exit.end,
			ring,
			scale,
			turns,
			...coreDiffers ? { coreTurns } : {}
		};
	}
};
/**
* Pose `ms` after loading started, in one mode throughout. `stopAt` is when
* the app asked to stop, or Infinity while still loading.
*/
function poseAt(ms, stopAt, mode = "stepped") {
	const timeline = new LoaderTimeline(mode);
	if (stopAt <= ms) timeline.stop(stopAt);
	return timeline.poseAt(ms);
}
const deg = (d) => d * Math.PI / 180;
function mul(a, b) {
	const row = (r) => [
		r[0] * b[0][0] + r[1] * b[1][0] + r[2] * b[2][0],
		r[0] * b[0][1] + r[1] * b[1][1] + r[2] * b[2][1],
		r[0] * b[0][2] + r[1] * b[1][2] + r[2] * b[2][2]
	];
	return [
		row(a[0]),
		row(a[1]),
		row(a[2])
	];
}
const apply = (m, v) => [
	m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
	m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
	m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2]
];
/** Rotation about a unit axis, same handedness as CSS `rotate3d` (y down). */
function rotation([x, y, z], degrees) {
	const c = Math.cos(deg(degrees));
	const s = Math.sin(deg(degrees));
	const t = 1 - c;
	return [
		[
			t * x * x + c,
			t * x * y - s * z,
			t * x * z + s * y
		],
		[
			t * x * y + s * z,
			t * y * y + c,
			t * y * z - s * x
		],
		[
			t * x * z - s * y,
			t * y * z + s * x,
			t * z * z + c
		]
	];
}
const SIGNS = [
	[-1, -1],
	[1, -1],
	[1, 1],
	[-1, 1]
];
/** Vector with `a` on `axis` and `b`, `c` on the next two axes in turn. */
function onAxis(axis, a, b, c) {
	const out = [
		0,
		0,
		0
	];
	out[axis] = a;
	out[(axis + 1) % 3] = b;
	out[(axis + 2) % 3] = c;
	return out;
}
const CUBE_FACES = [
	0,
	1,
	2
].flatMap((axis) => [1, -1].map((sign) => ({
	corners: SIGNS.map(([p, q]) => onAxis(axis, sign, p, q)),
	normal: onAxis(axis, sign, 0, 0)
})));
const CUBE_CORNERS = [-1, 1].flatMap((x) => [-1, 1].flatMap((y) => [-1, 1].map((z) => [
	x,
	y,
	z
])));
const HULL_EPSILON = 1e-9;
/** Convex hull, counter-clockwise in screen space (monotone chain). */
function convexHull(points) {
	const sorted = [...points].sort((a, b) => Math.abs(a[0] - b[0]) > HULL_EPSILON ? a[0] - b[0] : a[1] - b[1]);
	const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
	const half = (list) => {
		const out = [];
		for (const p of list) {
			while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= HULL_EPSILON) out.pop();
			out.push(p);
		}
		out.pop();
		return out;
	};
	return [...half(sorted), ...half([...sorted].reverse())];
}
const fmt = (n) => (Math.round(n * 1e3) / 1e3).toString();
const pt = ([x, y]) => `${fmt(x)} ${fmt(y)}`;
/**
* Convex polygon with each corner rounded the way the icon rounds its shell:
* a cubic whose tangent length is `radius * tan(turn / 2)`, capped so adjacent
* corners never overlap.
*/
function roundedPolygonPath(points, radius) {
	const n = points.length;
	let d = "";
	points.forEach((v, i) => {
		const prev = points[(i + n - 1) % n];
		const next = points[(i + 1) % n];
		const l1 = Math.hypot(v[0] - prev[0], v[1] - prev[1]);
		const l2 = Math.hypot(next[0] - v[0], next[1] - v[1]);
		const u1 = [(v[0] - prev[0]) / l1, (v[1] - prev[1]) / l1];
		const u2 = [(next[0] - v[0]) / l2, (next[1] - v[1]) / l2];
		const turn = Math.acos(Math.max(-1, Math.min(1, u1[0] * u2[0] + u1[1] * u2[1])));
		const t = Math.min(radius * Math.tan(turn / 2), l1 / 2, l2 / 2);
		const at = (u, k) => [v[0] + u[0] * k, v[1] + u[1] * k];
		d += `${i ? "L" : "M"}${pt(at(u1, -t))}C${pt(at(u1, -t * CORNER_HANDLE))} ${pt(at(u2, t * CORNER_HANDLE))} ${pt(at(u2, t))}`;
	});
	return `${d}Z`;
}
/** Pointy-top hexagon around the mark's centre. */
function hexagon(radius) {
	return [
		-90,
		-30,
		30,
		90,
		150,
		210
	].map((a) => [CENTER[0] + radius * Math.cos(deg(a)), CENTER[1] + radius * Math.sin(deg(a))]);
}
const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const cross2 = (a, b) => a[0] * b[1] - a[1] * b[0];
/** Resting cube silhouette, relative to CENTER, clockwise from the top. */
const REST_HULL = hexagon(CUBE_RADIUS).map((p) => sub(p, CENTER));
/**
* How far each resting corner moves to land on the icon, in REST_HULL order,
* plus the front corner. The icon never draws the top face, so its top corner
* completes the parallelogram of the two drawn faces.
*/
const REST_CORNER_SHIFT = [
	[ICON_CUBE.upperLeft[0] + ICON_CUBE.upperRight[0] - ICON_CUBE.center[0], ICON_CUBE.upperLeft[1] + ICON_CUBE.upperRight[1] - ICON_CUBE.center[1]],
	ICON_CUBE.upperRight,
	ICON_CUBE.lowerRight,
	ICON_CUBE.bottom,
	ICON_CUBE.lowerLeft,
	ICON_CUBE.upperLeft
].map((target, i) => sub(sub(target, CENTER), REST_HULL[i]));
const REST_CENTER_SHIFT = sub(ICON_CUBE.center, CENTER);
/**
* Piecewise-linear warp that takes the resting cube onto the icon's cube,
* applied with `weight`. It is keyed on screen position over the six triangles
* fanning out from the centre (extended linearly beyond the hull), so it stays
* continuous whichever cube faces happen to be in view.
*/
function restWarp(p, weight) {
	if (weight <= 0) return p;
	const rel = sub(p, CENTER);
	const angle = Math.atan2(rel[1], rel[0]) * 180 / Math.PI;
	const k = Math.floor(((angle + 90) % 360 + 360) % 360 / 60) % 6;
	const u = REST_HULL[k];
	const v = REST_HULL[(k + 1) % 6];
	const det = cross2(u, v);
	const a = cross2(rel, v) / det;
	const b = cross2(u, rel) / det;
	const du = REST_CORNER_SHIFT[k];
	const dv = REST_CORNER_SHIFT[(k + 1) % 6];
	const dc = REST_CENTER_SHIFT;
	return [p[0] + weight * (dc[0] + a * (du[0] - dc[0]) + b * (dv[0] - dc[0])), p[1] + weight * (dc[1] + a * (du[1] - dc[1]) + b * (dv[1] - dc[1]))];
}
/** One edge of the shell ring; at the shell or cavity it is the icon's own. */
function ringEdge(radius, corner) {
	if (radius === SHELL_RADIUS) return ICON_SHELL;
	if (radius === CAVITY_RADIUS) return ICON_CAVITY;
	return roundedPolygonPath(hexagon(radius), corner);
}
/** Draw a pose as SVG path data in icon viewBox units. */
function renderFrame(pose) {
	const orientationOf = (turns) => turns.reduceRight((m, [axis, degrees]) => mul(rotation(axis, degrees), m), rotation([
		0,
		1,
		0
	], REST_YAW_DEG));
	const tilt = rotation([
		1,
		0,
		0
	], -ISO_TILT_DEG);
	const orient = orientationOf(pose.turns);
	const view = mul(tilt, orient);
	const coreView = pose.coreTurns ? mul(tilt, orientationOf(pose.coreTurns)) : view;
	const g = clamp01((pose.scale - 1) / (PEAK_SCALE - 1));
	const project = (c, halfEdge, by = view) => {
		const p = apply(by, [
			c[0] * halfEdge * pose.scale,
			c[1] * halfEdge * pose.scale,
			c[2] * halfEdge * pose.scale
		]);
		return [CENTER[0] + p[0], CENTER[1] + p[1]];
	};
	const projectCube = (c) => restWarp(project(c, HALF_EDGE), 1 - g);
	const outline = roundedPolygonPath(convexHull(CUBE_CORNERS.map(projectCube)), SHELL_CORNER / PEAK_SCALE * pose.scale);
	const ring = pose.ring ? `${ringEdge(pose.ring.outer, pose.ring.outerCorner)}${ringEdge(pose.ring.inner, pose.ring.innerCorner)}` : "";
	if (pose.scale === 1 && pose.turns.length === 0) {
		const faces = [{
			d: ICON_RIGHT_FACE,
			opacity: ICON_RIGHT_FACE_OPACITY
		}, {
			d: ICON_LEFT_FACE,
			opacity: ICON_LEFT_FACE_OPACITY
		}];
		if (ring === "M6.47656 0.407989C7.41922 -0.135996 8.58078 -0.135996 9.52344 0.407989L14.4756 3.26541C15.4187 3.80968 15.9999 4.81617 16 5.90506V11.6179C16 12.7068 15.4186 13.7133 14.4756 14.2576L9.52344 17.116C8.5808 17.6599 7.4192 17.6599 6.47656 17.116L1.52441 14.2576C0.581406 13.7132 3.65972e-05 12.7068 0 11.6179V5.90506C0.000118328 4.81617 0.581295 3.80968 1.52441 3.26541L6.47656 0.407989ZM8.76172 3.81619C8.29039 3.5442 7.70961 3.54421 7.23828 3.81619L4.0957 5.62967C3.62422 5.90177 3.33315 6.40466 3.33301 6.949V10.575C3.33304 11.1195 3.62411 11.6231 4.0957 11.8953L7.23828 13.7088C7.70957 13.9807 8.29045 13.9807 8.76172 13.7088L11.9043 11.8953C12.3759 11.6231 12.666 11.1194 12.666 10.575V6.949C12.6659 6.40471 12.3757 5.90179 11.9043 5.62967L8.76172 3.81619Z") return {
			core: "",
			faces,
			outline: `M0 0H16V18H0Z`,
			ring: ring + ICON_CORE
		};
		return {
			core: ICON_CORE,
			faces,
			outline,
			ring
		};
	}
	const lid = LID_OPACITY * g;
	const faces = [];
	for (const { normal, corners } of CUBE_FACES) {
		if (apply(view, normal)[2] <= 1e-6) continue;
		const world = apply(orient, normal);
		const opacity = clamp01(SHADE_BASE - SHADE_SIDE * world[0] + (lid - SHADE_BASE) * -world[1]);
		if (opacity < .005) continue;
		faces.push({
			d: `M${corners.map(projectCube).map(pt).join("L")}Z`,
			opacity
		});
	}
	faces.sort((a, b) => a.opacity - b.opacity);
	return {
		core: roundedPolygonPath(convexHull(CUBE_CORNERS.map((c) => project(c, CORE_HALF_EDGE, coreView))), CORE_CORNER * pose.scale),
		faces,
		outline,
		ring
	};
}
//#endregion
export { LoaderTimeline, MODES, TIMING, VIEWBOX_HEIGHT, VIEWBOX_WIDTH, convexHull, hexagon, poseAt, renderFrame, roundedPolygonPath };

//# sourceMappingURL=core-intelligence-loader-geometry.js.map